/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : VlcIndex.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
} from "@mui/material";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import setSelectedProject from "../../../redux/actions/selectProject";
import CommonBreadcrumbs from "../common/Breadcrumbs/CommonBreadcrumbs";
import {
  HandleApiActions,
  labCar,
  profileActions,
  userManagementApis,
} from "../../../redux/actions";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import VlcMain from "./pages/VlcMain";
import VlcCarCard from "../../../pages/labcar/CarCard";
import VlcVehicleSystemsIndex from "./VlcVehicleSystemsIndex";
import ProjectVariantSearchbar from "../common/projectSearchbar/ProjectvariantSearchbar";
import ProjectVariantDialog from "./ProjectVarientAccordian/ProjectVariantDialog";
import CommonInputSwitch from "../common/InputSwitch/CommonInputSwitch";
import { PROJECT_STATUS } from "../../../config/appConstants";
import CustomButtonV2 from "../common/button/CustomButtonV2";
import CustomDialogBoxV2 from "../common/customDialogBox/CustomDialogBoxV2";
import ChangeParentDialog from "./pages/ChangeParentDialog";


function VlcIndex({ drawerWidth = 240, drawerOpen = true }) {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);

  const selectedZoneFromUrl = queryParams.get("selectedZone");
  const zonePositionFromUrl = queryParams.get("zonePosition");

  const dispatch = useDispatch();


  const selectedProject = useSelector(
    (state) => state?.projectConfigReducer?.selectedProjectDetails,
  );
  const selectedVariant = useSelector(
    (state) => state.projectConfigReducer.selectedVariantDetails,
  );

  const { eeComponentsList } = useSelector(
    (state) => state.labCarReducer,
    shallowEqual,
  );

  const variants = useSelector((state) => state.projectConfigReducer.variants);


  const settingsVehicleSystemsDetails = useSelector(
    (state) => state.vehicleSystemsReducer,
  );

  const profileDetails = useSelector((store) => store.profileReducer);

  const roleType = profileDetails?.profile?.role_details?.role_type;

  const supplier = roleType === 2;

  const [projectOptionsList, setProjectOptionsList] = useState([]);
  const [variantOptionList, setVariantOptionList] = useState([]);
  const [selectedProjectDetails, setSelectedProjectDetails] = useState(null);
  const [selectedSingleVariantDetails, setSelectedSingleVariantDetails] = useState([]);
  const [selectedVehicleView, setSelectedVehicleView] = useState("ECU");

  const [availableZones, setAvailableZones] = useState([]);
  const [selectedZone, setSelectedZone] = useState(null);
  const [zoneEECountList, setZoneEECountList] = useState([]);
  const [zoneVSCountList, setZoneVSCountList] = useState([]);
  const [centralizedEEList, setCentralizedEEList] = useState([]);
  const [selectedEEComponentDetails, setSelectedEEComponentDetails] = useState(null);
  const [vsAndVCOptionsList, setVSAndVCOptionsList] = useState([]);
  const [selectedZoneDetails, setSelectedZoneDetails] = useState(null);
  const [viewArchived, setViewArchived] = useState(false);
  const [selectedArchivedEEs, setSelectedArchivedEEs] = useState([]);
  const [selectedArchivedSWMs, setSelectedArchivedSWMs] = useState([]);
  const [selectedArchivedVSs, setSelectedArchivedVSs] = useState([]);
  const [selectedArchivedVCs, setSelectedArchivedVCs] = useState([]);
  const [retrievalRefreshCounter, setRetrievalRefreshCounter] = useState(0);
  const [clearAllCounter, setClearAllCounter] = useState(0);
  const [isChangeParentDialogOpen, setIsChangeParentDialogOpen] = useState(false);
  const [confirmRetrievalDialogOpen, setConfirmRetrievalDialogOpen] =
    useState(false);
  const appliedUrlZoneKeyRef = useRef(null);
  const prevProjectIdRef = useRef(selectedProject?.project_id);
  const prevVariantIdRef = useRef(selectedVariant?.variant_id);
  const prevPathnameRef = useRef(location.pathname);

  useEffect(() => {
    if (prevPathnameRef.current !== location.pathname) {
      setSelectedArchivedEEs([]);
      setSelectedArchivedSWMs([]);
      setSelectedArchivedVCs([]);
      setSelectedArchivedVSs([]);
      prevPathnameRef.current = location.pathname;
    }
  }, [location.pathname]);

  useEffect(() => {
    const currentProjectId = selectedProject?.project_id;
    const currentVariantId = selectedVariant?.variant_id;

    const isProjectChanged =
      Boolean(prevProjectIdRef.current) &&
      Boolean(currentProjectId) &&
      prevProjectIdRef.current !== currentProjectId;

    const isVariantChanged =
      Boolean(prevVariantIdRef.current) &&
      Boolean(currentVariantId) &&
      prevVariantIdRef.current !== currentVariantId;

    if (isProjectChanged || isVariantChanged) {

      if (location.search) {
        const params = new URLSearchParams(location.search);
        params.delete("ee_id");
        params.delete("swm_id");
        params.delete("zone");
        params.delete("selectedZone");
        params.delete("zonePosition");
        params.delete("actionMode");
        params.delete("page");
        params.delete("linkModuleType");

        const newSearch = params.toString();
        navigate(
          newSearch ? `${location.pathname}?${newSearch}` : location.pathname,
          { replace: true },
        );
      }
    }

    if (currentProjectId) {
      prevProjectIdRef.current = currentProjectId;
    }
    if (currentVariantId) {
      prevVariantIdRef.current = currentVariantId;
    }
  }, [
    selectedProject?.project_id,
    selectedVariant?.variant_id,
    location.pathname,
    location.search,
    navigate,
  ]);








  useEffect(() => {
    if (location?.pathname?.includes("vehicle-systems")) {
      setSelectedVehicleView("VEHICLE");
    } else if (location?.pathname?.includes("EESWM")) {
      setSelectedVehicleView("ECU");
    }
  }, [location?.pathname]);

  useEffect(() => {
    const goingToEE = location?.pathname?.includes("EESWM");
    if (!goingToEE) return;
    if (!selectedProjectDetails?.is_zonal) return;
    if (selectedZoneFromUrl || zonePositionFromUrl) return;

    const currentName = selectedZone?.zone_position_name?.trim();
    const defaultZoneName = availableZones?.[0]?.position_name?.trim();

    if (!currentName && defaultZoneName) {
      setSelectedZone({ zone_position_name: defaultZoneName });
    }
  }, [
    location?.pathname,
    selectedProjectDetails?.is_zonal,
    availableZones,
    selectedZoneFromUrl,
    zonePositionFromUrl,
    selectedZone?.zone_position_name,
  ]);

  useEffect(() => {
    const goingToEE = location?.pathname?.includes("EESWM");
    if (!goingToEE || !selectedProjectDetails?.is_zonal) return;
    if (!selectedZoneFromUrl && !zonePositionFromUrl) return;
    const urlZoneKey = `${selectedZoneFromUrl || ""}|${zonePositionFromUrl || ""
      }`;
    if (appliedUrlZoneKeyRef.current === urlZoneKey) return;

    const matchedZone = selectedProjectDetails?.project_zones?.find((zone) => {
      return (
        zone?.zone_name === selectedZoneFromUrl ||
        String(zone?.position) === String(selectedZoneFromUrl) ||
        String(zone?.position) === String(zonePositionFromUrl)
      );
    });
    const zoneName = matchedZone?.zone_name || selectedZoneFromUrl;

    if (zoneName && selectedZone?.zone_position_name !== zoneName) {
      setSelectedZone({ zone_position_name: zoneName });
    }
    appliedUrlZoneKeyRef.current = urlZoneKey;
  }, [
    location?.pathname,
    selectedProjectDetails?.is_zonal,
    selectedProjectDetails?.project_zones,
    selectedZoneFromUrl,
    zonePositionFromUrl,
    selectedZone?.zone_position_name,
  ]);

  useEffect(() => {
    const goingToEE = location?.pathname?.includes("EESWM");
    if (!goingToEE) return;
    if (!selectedProjectDetails?.is_zonal) return;
    if (selectedZoneFromUrl || zonePositionFromUrl) return;
    if (selectedZone?.zone_position_name) return;
    const firstZoneName = availableZones?.[0]?.position_name;
    if (firstZoneName) {
      setSelectedZone({ zone_position_name: firstZoneName });
    }
  }, [
    zoneEECountList,
    location?.pathname,
    availableZones,
    selectedProjectDetails?.is_zonal,
    selectedZoneFromUrl,
    zonePositionFromUrl,
    selectedZone?.zone_position_name,
  ]);

  useEffect(() => {
    const filteredVariantOptionsList = variants.map((el) => ({
      value: el.variant_id,
      label: el.name,
      color: el.color_code,
    }));
    setVariantOptionList(filteredVariantOptionsList);
  }, [variants]);


  useEffect(() => {
    dispatch(HandleApiActions({ ...labCar.get_project_details }));
    dispatch(
      HandleApiActions({
        ...userManagementApis.get_roles_list,
        params: {
          page_number: 1,
          page_size: 100,
          sort_column: "created_at",
          sort_order: "ASC",
        },
        show_toast: false,
      }),
    );
    dispatch(HandleApiActions({ ...profileActions.get_profile_details }));
  }, [dispatch]);


  const getZoneEECountList = useCallback(
    async (projectId, variantId, archiveOption) => {
      if (!projectId || !variantId) {
        setZoneEECountList([]);
        return;
      }
      const archive =
        typeof archiveOption === "boolean" ? archiveOption : Boolean(viewArchived);
      const resp = await Promise.resolve(dispatch(
        HandleApiActions({
          ...labCar.get_ee_components_count,
          params: {
            project_id: projectId,
            variant_id: variantId,
            archive: Boolean(archive),
            page_number: 1,
            page_size: 10000,
          },
          show_toast: false,
        }),
      ));
      if (resp.error) {
        setZoneEECountList([]);
      } else if (resp?.data?.length > 0) {
        const components = resp?.data;
        const counts = components?.reduce((acc, component) => {
          const position = component?.zone_details?.position;
          if (position) {
            acc[position] = (acc[position] || 0) + 1;
          }
          return acc;
        }, {});

        const filteredZoneEECountList = Object?.entries(counts)?.map(
          ([zone_position, ee_components_count]) => ({
            zone_position,
            ee_components_count,
          }),
        );
        setZoneEECountList(filteredZoneEECountList || []);
      } else {
        setZoneEECountList([]);
      }
    },
    [dispatch, viewArchived],
  );

  const getZoneVSCountList = useCallback(
    async (archiveOption) => {
      const projectId = selectedProjectDetails?.project_id;
      const variantId = selectedSingleVariantDetails?.variant_id;
      if (!projectId || !variantId) {
        setZoneVSCountList([]);
        return;
      }
      const archive =
        typeof archiveOption === "boolean" ? archiveOption : Boolean(viewArchived);
      const apiDetails = labCar.get_vehicle_systems_all;
      const resp = await Promise.resolve(dispatch(
        HandleApiActions({
          ...apiDetails,
          show_toast: false,
          params: {
            project_id: Number(projectId),
            variant_id: Number(variantId),
            archive: Boolean(archive),
            page_number: 1,
            page_size: 10000,
          },
        }),
      ));
      if (resp.error) {
        setZoneVSCountList([]);
      } else if (resp?.data?.length > 0) {
        const components = resp?.data;
        const counts = components?.reduce((acc, component) => {
          const position = component?.zone_details?.[0]?.position;
          if (position) {
            acc[position] = (acc[position] || 0) + 1;
          }
          return acc;
        }, {});

        const filteredZoneVSCountList = Object?.entries(counts)?.map(
          ([zone_position, vehicle_systems_count]) => ({
            zone_position,
            vehicle_systems_count,
          }),
        );
        setZoneVSCountList(filteredZoneVSCountList || []);
      } else {
        setZoneVSCountList([]);
      }
    },
    [
      dispatch,
      selectedProjectDetails?.project_id,
      selectedSingleVariantDetails?.variant_id,
      viewArchived,
    ],
  );

  useEffect(() => {
    if (selectedProjectDetails?.project_id !== selectedProject?.project_id) {
      setAvailableZones([]);
      setSelectedZone(null);
      setSelectedZoneDetails(null);
      setCentralizedEEList([]);
      setZoneEECountList([]);
      setZoneVSCountList([]);
      setSelectedSingleVariantDetails([]);
      if (lastCountsFetchRef?.current) {
        lastCountsFetchRef.current = {
          projectId: null,
          variantId: null,
          viewArchived: null,
          retrievalRefreshCounter: 0,
        };
      }
    }
  }, [selectedProject, selectedProjectDetails?.project_id]);

  const prevCountsVariantIdRef = useRef(null);
  const prevCountsViewArchivedRef = useRef(viewArchived);
  useEffect(() => {
    const currentVariantId = selectedSingleVariantDetails?.variant_id;
    if (
      (prevCountsVariantIdRef.current &&
        currentVariantId &&
        prevCountsVariantIdRef.current !== currentVariantId) ||
      prevCountsViewArchivedRef.current !== viewArchived
    ) {
      setZoneEECountList([]);
      setZoneVSCountList([]);
      setCentralizedEEList([]);
    }
    prevCountsVariantIdRef.current = currentVariantId;
    prevCountsViewArchivedRef.current = viewArchived;
  }, [selectedSingleVariantDetails?.variant_id, viewArchived]);

  const lastCountsFetchRef = useRef({
    projectId: null,
    variantId: null,
    viewArchived: null,
    retrievalRefreshCounter: 0,
  });

  useEffect(() => {
    const projectId = selectedProjectDetails?.project_id;
    const variantId = selectedSingleVariantDetails?.variant_id;
    if (!projectId || !variantId) return;
    const variantProjectId = selectedSingleVariantDetails?.project_id;
    if (variantProjectId && Number(variantProjectId) !== Number(projectId)) {
      return;
    }

    const {
      projectId: prevPid,
      variantId: prevVid,
      viewArchived: prevArchived,
      retrievalRefreshCounter: prevRefreshCounter,
    } = lastCountsFetchRef.current;
    if (
      prevPid === projectId &&
      prevVid === variantId &&
      prevArchived === viewArchived &&
      prevRefreshCounter === retrievalRefreshCounter
    ) {
      return;
    }

    lastCountsFetchRef.current = {
      projectId,
      variantId,
      viewArchived,
      retrievalRefreshCounter,
    };
    getZoneEECountList(projectId, variantId, viewArchived).catch(console.error);
    getZoneVSCountList(viewArchived).catch(console.error);
  }, [
    selectedProjectDetails?.project_id,
    selectedSingleVariantDetails?.variant_id,
    selectedSingleVariantDetails?.project_id,
    viewArchived,
    retrievalRefreshCounter,
    getZoneEECountList,
    getZoneVSCountList,
  ]);

  useEffect(() => {
    if (!selectedProjectDetails) return;
    const projectId = selectedProjectDetails?.project_id;
    const variantId = selectedSingleVariantDetails?.variant_id;
    const variantProjectId = selectedSingleVariantDetails?.project_id;

    const hasVariant = Boolean(variantId);
    const isVariantForProject =
      projectId && variantProjectId
        ? Number(projectId) === Number(variantProjectId)
        : true;

    if (!hasVariant || !isVariantForProject) {
      setCentralizedEEList([]);
      return;
    }

    if (!selectedProjectDetails.is_zonal) {
      setCentralizedEEList(Array.isArray(eeComponentsList) ? eeComponentsList : []);
    }
  }, [
    eeComponentsList,
    selectedProjectDetails,
    selectedSingleVariantDetails?.variant_id,
    selectedSingleVariantDetails?.project_id,
  ]);

  useEffect(() => {
    setSelectedProjectDetails(selectedProject);
    setViewArchived(false);
    console.log("🔍 [VlcIndex] selectedProjectDetails / selectedProject:", {
      status: selectedProject?.status,
      archive_status: selectedProject?.archive_status,
      archive_vlc: selectedProject?.archive_vlc,
      fullObject: selectedProject,
    });
  }, [selectedProject]);

  useEffect(() => {
    const projectId =
      selectedProjectDetails?.project_id || selectedProject?.project_id;
    const variantId = selectedVariant?.variant_id;
    const variantProjectId = selectedVariant?.project_id;
    const hasVariants = Array.isArray(variants) && variants.length > 0;

    if (!projectId || !hasVariants || !variantId) {
      setSelectedSingleVariantDetails([]);
      return;
    }

    if (variantProjectId && Number(variantProjectId) !== Number(projectId)) {
      setSelectedSingleVariantDetails([]);
      return;
    }

    setSelectedSingleVariantDetails(selectedVariant);
  }, [
    selectedVariant,
    selectedProjectDetails?.project_id,
    selectedProject?.project_id,
    variants,
  ]);

  useEffect(() => {
    if (selectedProjectDetails?.is_zonal) {

      let filteredAvailableZones = selectedProjectDetails?.project_zones?.map(
        (el) => {
          return {
            zone_position: el.position,
            position_name: el.zone_name,
          };
        },
      );
      setAvailableZones(filteredAvailableZones);
    }
  }, [selectedProjectDetails]);

  useEffect(() => {
    if (selectedZone?.zone_position_name && selectedProjectDetails?.project_zones) {
      let filteredSelectedZoneDetails =
        selectedProjectDetails?.project_zones?.find(
          (el) => el?.zone_name === selectedZone?.zone_position_name,
        );
      setSelectedZoneDetails(filteredSelectedZoneDetails);
    }
  }, [selectedZone, selectedProjectDetails]);

  useEffect(() => {
    let filteredVehicleSystemOptionsList = Array.isArray(
      settingsVehicleSystemsDetails?.vehicleSystemsDetails?.data,
    )
      ? settingsVehicleSystemsDetails.vehicleSystemsDetails.data.map((vs) => ({
        value: vs?.name,
        label: vs?.name,
        vehicleComponentsOptionsList: Array.isArray(vs?.components)
          ? vs.components.map((vc) => ({
            value: vc,
            label: vc,
          }))
          : [],
      }))
      : [];

    setVSAndVCOptionsList(filteredVehicleSystemOptionsList);
  }, [settingsVehicleSystemsDetails]);

const getRetrievalConfig = ({
  projectId,
  variantId,
  selectedArchivedVCs,
  selectedArchivedVSs,
  selectedArchivedSWMs,
  selectedArchivedEEs,
}) => {
  if (selectedArchivedVCs?.length > 0) {
    return {
      type: "VC",
      apiAction: labCar.retrieve_vehicle_components,
      payload: { project_id: projectId, variant_id: variantId, vc_ids: selectedArchivedVCs },
    };
  }
  if (selectedArchivedVSs?.length > 0) {
    return {
      type: "VS",
      apiAction: labCar.retrieve_vehicle_systems,
      payload: { project_id: projectId, variant_id: variantId, vs_ids: selectedArchivedVSs },
    };
  }
  if (selectedArchivedSWMs?.length > 0) {
    return {
      type: "SWM",
      apiAction: labCar.retrieve_software_modules,
      payload: { project_id: projectId, variant_id: variantId, swm_ids: selectedArchivedSWMs },
    };
  }
  if (selectedArchivedEEs?.length > 0) {
    return {
      type: "EE",
      apiAction: labCar.retrieve_ee_components,
      payload: { project_id: projectId, variant_id: variantId, ee_ids: selectedArchivedEEs },
    };
  }
  return null;
};

const handlePostRetrievalSuccess = ({
  type,
  setSelectedArchivedVCs,
  setSelectedArchivedVSs,
  setSelectedArchivedSWMs,
  setSelectedArchivedEEs,
  setClearAllCounter,
  setRetrievalRefreshCounter,
  isZonal,
  getZoneVSCountList,
  getZoneEECountList,
  projectId,
  variantId,
}) => {
  setClearAllCounter((prev) => prev + 1);
  if (typeof setRetrievalRefreshCounter === "function") {
    setRetrievalRefreshCounter((prev) => prev + 1);
  }

  if (type === "VC") {
    setSelectedArchivedVCs([]);
    if (isZonal) getZoneVSCountList();
  } else if (type === "VS") {
    setSelectedArchivedVSs([]);
    if (isZonal) getZoneVSCountList();
  } else if (type === "SWM") {
    setSelectedArchivedSWMs([]);
    if (isZonal && projectId && variantId) getZoneEECountList(projectId, variantId);
  } else if (type === "EE") {
    setSelectedArchivedEEs([]);
    if (isZonal && projectId && variantId) getZoneEECountList(projectId, variantId);
  }
};

  const getRetrievalConfirmMessage = () => {
    const config = getRetrievalConfig({
      projectId: selectedProjectDetails?.project_id || selectedProject?.project_id,
      variantId: selectedSingleVariantDetails?.variant_id || selectedVariant?.variant_id,
      selectedArchivedVCs,
      selectedArchivedVSs,
      selectedArchivedSWMs,
      selectedArchivedEEs,
    });
    switch (config?.type) {
      case "EE":
        return "Retrieving the component will also retrieve eligible dependencies and intersections.";
      case "SWM":
        return "Retrieving the component will also retrieve eligible dependencies and intersections.";
      case "VS":
        return "Retrieving the component will also retrieve eligible intersections.";
      case "VC":
        return "Retrieving the component will also retrieve eligible intersections.";
      default:
        return "This change will affect the downstream components, modules, dependencies and intersections, if any are linked to the element.";
    }
  };

  const handleConfirmRetrieval = async () => {
    const projectId = selectedProjectDetails?.project_id || selectedProject?.project_id;
    const variantId = selectedSingleVariantDetails?.variant_id || selectedVariant?.variant_id;

    if (!projectId || !variantId) return;

    const retrievalConfig = getRetrievalConfig({
      projectId,
      variantId,
      selectedArchivedVCs,
      selectedArchivedVSs,
      selectedArchivedSWMs,
      selectedArchivedEEs,
    });

    if (!retrievalConfig) return;

    try {
      const res = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...retrievalConfig.apiAction,
            params: retrievalConfig.payload,
            show_toast: true,
          })
        )
      );

      if (res && !res.error) {
        handlePostRetrievalSuccess({
          type: retrievalConfig.type,
          setSelectedArchivedVCs,
          setSelectedArchivedVSs,
          setSelectedArchivedSWMs,
          setSelectedArchivedEEs,
          setClearAllCounter,
          setRetrievalRefreshCounter,
          isZonal: selectedProjectDetails?.is_zonal,
          getZoneVSCountList,
          getZoneEECountList,
          projectId,
          variantId,
        });
      }
    } catch (err) {
      console.error("Error confirming retrieval:", err);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        width: "100%",
        overflow: "hidden",
      }}
    >
      <Box sx={{ p: 1, flexShrink: 0 }}>
        <CommonBreadcrumbs
          items={[
            { label: "Virtual Lab-Car" },
            location?.pathname?.includes("EESWM")
              ? { label: "EE Architecture" }
              : { label: "Vehicle Systems" },
          ]}
        />
      </Box>

      <Box
        sx={{
          flexShrink: 0,
          display: "flex",
          gap: 1.5,
          ml: 1,
          mb: 1,
          alignItems: "center",
        }}
      >
        <Box>
          <ProjectVariantSearchbar />
        </Box>
        <Box>
          <ProjectVariantDialog />
        </Box>
        {selectedProjectDetails?.status === PROJECT_STATUS.DRAFT &&
          Boolean(selectedProjectDetails?.archive_vlc) && (
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-end",
                width: "100%",
                justifyContent: "flex-end",
                mr: 1,
              }}
            >
              <CommonInputSwitch
                label="View Archived"
                checked={viewArchived}
                onChange={setViewArchived}
              />
            </Box>
          )}
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "auto",
          display: "flex",
          gap: 1,
          px: 1,
          py: 1,
          boxSizing: "border-box",
        }}
      >
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            width: "auto",
            maxWidth: { xs: "100%", md: "420px" },
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            boxSizing: "border-box",
          }}
        >
          {supplier ? (
            selectedProjectDetails?.is_zonal && (
              <VlcCarCard
                availableZones={availableZones}
                selectedComponent={selectedZone}
                setSelectedComponent={setSelectedZone}
                zoneEECountList={zoneEECountList}
                zoneVSCountList={zoneVSCountList}
                selectedVehicleView={selectedVehicleView}
                selectedProjectIsZonal={selectedProjectDetails?.is_zonal}
                centralizedEEList={centralizedEEList}
                selectedEEComponentDetails={selectedEEComponentDetails}
                selectedSingleVariantDetails={selectedSingleVariantDetails}
                viewArchived={viewArchived}
              />
            )
          ) : (
            <VlcCarCard
              vsAndVCOptionsList={vsAndVCOptionsList}
              availableZones={availableZones}
              selectedComponent={selectedZone}
              setSelectedComponent={setSelectedZone}
              zoneEECountList={zoneEECountList}
              zoneVSCountList={zoneVSCountList}
              selectedVehicleView={selectedVehicleView}
              centralizedEEList={centralizedEEList}
              selectedEEComponentDetails={selectedEEComponentDetails}
              selectedSingleVariantDetails={selectedSingleVariantDetails}
              viewArchived={viewArchived}
            />
          )}
        </Box>

        <Box
          sx={{
            flex: 2,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {viewArchived && (
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-end",
                width: "100%",
                justifyContent: "flex-end",
                mr: 1,
                mb: 1,
                gap: 1,
              }}
            >
              <CustomButtonV2
                btnName="Clear all"
                disabled={selectedArchivedEEs?.length === 0 && selectedArchivedSWMs?.length === 0 && selectedArchivedVSs?.length === 0 && selectedArchivedVCs?.length === 0}
                handleOnClick={(e) => {
                  e?.stopPropagation?.();
                  setSelectedArchivedEEs([]);
                  setSelectedArchivedSWMs([]);
                  setSelectedArchivedVSs([]);
                  setSelectedArchivedVCs([]);
                  setClearAllCounter((prev) => prev + 1);
                }}
                onClick={(e) => {
                  e?.stopPropagation?.();
                  setSelectedArchivedEEs([]);
                  setSelectedArchivedSWMs([]);
                  setSelectedArchivedVSs([]);
                  setSelectedArchivedVCs([]);
                  setClearAllCounter((prev) => prev + 1);
                }}
              />
              <CustomButtonV2
                btnName="Change parent"
                disabled={
                  selectedArchivedEEs?.length === 0 &&
                  selectedArchivedSWMs?.length === 0 &&
                  selectedArchivedVSs?.length === 0 &&
                  selectedArchivedVCs?.length === 0
                }
                handleOnClick={(e) => {
                  e.stopPropagation();
                  console.log("Change parent clicked", selectedArchivedEEs);
                  setIsChangeParentDialogOpen(true);
                }}  
              />
              <CustomButtonV2
                btnName="Confirm Retrival"
                disabled={
                  selectedArchivedEEs?.length === 0 &&
                  selectedArchivedSWMs?.length === 0 &&
                  selectedArchivedVSs?.length === 0 &&
                  selectedArchivedVCs?.length === 0
                }
                handleOnClick={(e) => {
                  e.stopPropagation();
                  setConfirmRetrievalDialogOpen(true);
                }}
              />
            </Box>
          )}
          <ChangeParentDialog
            open={isChangeParentDialogOpen}
            onClose={() => setIsChangeParentDialogOpen(false)}
            onSuccess={(transferConfig) => {
              const projectId =
                selectedProjectDetails?.project_id || selectedProject?.project_id;
              const variantId =
                selectedSingleVariantDetails?.variant_id ||
                selectedVariant?.variant_id;
              let type = "SWM";
              if (selectedArchivedVSs?.length) type = "VS";
              else if (selectedArchivedVCs?.length) type = "VC";
              else if (selectedArchivedEEs?.length) type = "EE";

              handlePostRetrievalSuccess({
                type,
                setSelectedArchivedVCs,
                setSelectedArchivedVSs,
                setSelectedArchivedSWMs,
                setSelectedArchivedEEs,
                setClearAllCounter,
                setRetrievalRefreshCounter,
                isZonal: selectedProjectDetails?.is_zonal,
                getZoneVSCountList,
                getZoneEECountList,
                projectId,
                variantId,
              });
            }}
            selectedArchivedEEs={selectedArchivedEEs}
            selectedArchivedSWMs={selectedArchivedSWMs}
            selectedArchivedVSs={selectedArchivedVSs}
            selectedArchivedVCs={selectedArchivedVCs}
          />
          {confirmRetrievalDialogOpen && (
            <CustomDialogBoxV2
              open={confirmRetrievalDialogOpen}
              onClose={() => setConfirmRetrievalDialogOpen(false)}
              title="Confirm Changes"
              successButtonName="Confirm"
              rejectButtonName="Cancel"
              actionButtonStyle="continuous"
              onClickSuccess={async () => {
                setConfirmRetrievalDialogOpen(false);
                await handleConfirmRetrieval();
              }}
              onClickReject={() => setConfirmRetrievalDialogOpen(false)}
            >
              <Box
                sx={{
                  p: 2,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "left",
                  textAlign: "left",
                  gap: 1,
                }}
              >
                <Typography>
                  {getRetrievalConfirmMessage()}
                </Typography>
                <Typography>
                  Are you sure you want to proceed?
                </Typography>
              </Box>
            </CustomDialogBoxV2>
          )}
          <Routes>
            <Route
              path="EESWM"
              element={
                <VlcMain
                  selectedArchivedSWMs={selectedArchivedSWMs}
                  setSelectedArchivedSWMs={setSelectedArchivedSWMs}
                  selectedArchivedEEs={selectedArchivedEEs}
                  setSelectedArchivedEEs={setSelectedArchivedEEs}
                  selectedSingleVariantDetails={selectedSingleVariantDetails}
                  setSelectedSingleVariantDetails={
                    setSelectedSingleVariantDetails
                  }
                  projectOptionsList={projectOptionsList}
                  variantOptionList={variantOptionList}
                  setProjectOptionsList={setProjectOptionsList}
                  selectedVariant={selectedVariant}
                  selectedProject={selectedProject}
                  setSelectedProject={setSelectedProject}
                  selectedVariantDetails={selectedVariant}
                  selectedProjectDetails={selectedProject}
                  setSelectedVehicleView={setSelectedVehicleView}
                  selectedVehicleView={selectedVehicleView}
                  selectedEEComponentDetails={selectedEEComponentDetails}
                  setSelectedEEComponentDetails={setSelectedEEComponentDetails}
                  selectedZone={selectedZone}
                  setSelectedZone={setSelectedZone}
                  selectedZoneDetails={selectedZoneDetails}
                  setSelectedZoneDetails={setSelectedZoneDetails}
                  zoneEECountList={zoneEECountList}
                  setZoneEECountList={setZoneEECountList}
                  getZoneEECountList={getZoneEECountList}
                  viewArchived={viewArchived}
                  clearAllCounter={clearAllCounter}
                  retrievalRefreshCounter={retrievalRefreshCounter}
                />
              }
            />
            <Route
              path="vehicle-systems"
              element={
                <VlcVehicleSystemsIndex
                  selectedProjectDetails={selectedProjectDetails}
                  selectedSingleVariantDetails={selectedSingleVariantDetails}
                  selectedZoneDetails={selectedZoneDetails}
                  variantOptionList={variantOptionList}
                  vsAndVCOptionsList={vsAndVCOptionsList}
                  getZoneVSCountList={getZoneVSCountList}
                  viewArchived={viewArchived}
                  selectedArchivedVSs={selectedArchivedVSs}
                  setSelectedArchivedVSs={setSelectedArchivedVSs}
                  selectedArchivedVCs={selectedArchivedVCs}
                  setSelectedArchivedVCs={setSelectedArchivedVCs}
                  clearAllCounter={clearAllCounter}
                  retrievalRefreshCounter={retrievalRefreshCounter}
                />
              }
            />
          </Routes>
        </Box>
      </Box>
    </Box>
  );
}

VlcIndex.propTypes = {
  drawerWidth: PropTypes.number,
  drawerOpen: PropTypes.bool,
};

export default VlcIndex;
